import React from 'react';
import ActionDropdown from './ActionDropdown.js';

const LoadingSkeleton = ({ columns }) => (
  <div className="p-6 space-y-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex gap-4 animate-pulse">
        {columns.map((_, j) => (
          <div key={j} className="h-4 bg-gray-200 rounded flex-1" />
        ))}
      </div>
    ))}
  </div>
);

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No records found',
  onRowClick,
  rowKey = 'id',
  actions,
  mobileCardRender,
  className = '',
  getRowClassName,
}) => {
  const getRowKey = (row, index) => row[rowKey] ?? row.id ?? index;

  const visibleColumns = columns.filter((col) => col.visible !== false);

  if (loading) {
    return (
      <div className={`bg-white rounded-card shadow-card overflow-hidden ${className}`}>
        <LoadingSkeleton columns={visibleColumns} />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={`bg-white rounded-card shadow-card p-8 text-center text-gray-500 ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-card shadow-card overflow-hidden w-full min-w-0 ${className}`}>
      {/* Desktop Table */}
      <div className="hidden md:block app-table-wrap">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 sm:px-4 py-2.5 sm:py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider whitespace-nowrap ${col.headerClassName || ''}`}
                  style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider w-[80px]">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                onClick={() => onRowClick?.(row)}
                className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${getRowClassName ? getRowClassName(row, index) : ''}`}
              >
                {visibleColumns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-sm text-secondary ${col.cellClassName || ''}`}
                  >
                    {col.render ? col.render(row, index) : row[col.key]}
                  </td>
                ))}
                {actions && (
                  <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                    <ActionDropdown actions={actions(row, index)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-100">
        {data.map((row, index) => (
          <div
            key={getRowKey(row, index)}
            onClick={() => onRowClick?.(row)}
            className={`p-4 ${onRowClick ? 'cursor-pointer active:bg-gray-50' : ''} ${getRowClassName ? getRowClassName(row, index) : ''}`}
          >
            {mobileCardRender ? (
              mobileCardRender(row, index)
            ) : (
              <div className="space-y-2">
                {visibleColumns.map((col) => (
                  <div key={col.key} className="flex justify-between items-start gap-2">
                    <span className="text-xs font-medium text-gray-500 shrink-0">{col.label}</span>
                    <span className="text-sm text-secondary text-right">
                      {col.render ? col.render(row, index) : row[col.key]}
                    </span>
                  </div>
                ))}
                {actions && (
                  <div className="flex justify-end pt-2" onClick={(e) => e.stopPropagation()}>
                    <ActionDropdown actions={actions(row, index)} />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DataTable;
